import {
  spaceAvailabilityBlockTypes,
  type SpaceAvailabilityBlockType,
} from "../../../packages/foundation/src/index.js";

export type SpaceAvailabilityBlockEditorInput = {
  blockId: string;
  spaceId: string;
  type: string;
  startsAt: string;
  endsAt: string;
  label: string;
  labelAr: string;
  internalReason: string;
  publicReason: string;
};

export type SpaceAvailabilityBlockEditorDraft = {
  blockId: string;
  spaceId: string;
  type: SpaceAvailabilityBlockType;
  startsAt: Date;
  endsAt: Date;
  label: {
    en: string;
    ar: string | null;
  };
  internalReason: string | null;
  publicReason: boolean;
  source: "experience_thirty_nine";
};

export type SpaceAvailabilityBlockEditorResult =
  | {
      ok: true;
      draft: SpaceAvailabilityBlockEditorDraft;
    }
  | {
      ok: false;
      errors: string[];
    };

export const spaceAvailabilityBlockTypeOptions = spaceAvailabilityBlockTypes;

const idLimit = 80;
const labelLimit = 120;
const reasonLimit = 500;

export function buildDefaultSpaceAvailabilityBlockEditorInput(): SpaceAvailabilityBlockEditorInput {
  return {
    blockId: "community-hall-maintenance",
    endsAt: "2026-10-24T20:00",
    internalReason: "Staff setup and maintenance hold.",
    label: "Maintenance hold",
    labelAr: "حجز صيانة",
    publicReason: "true",
    spaceId: "community-hall",
    startsAt: "2026-10-24T18:00",
    type: "maintenance",
  };
}

export function buildEmptySpaceAvailabilityBlockEditorInput(): SpaceAvailabilityBlockEditorInput {
  return {
    blockId: "",
    endsAt: "",
    internalReason: "",
    label: "",
    labelAr: "",
    publicReason: "false",
    spaceId: "",
    startsAt: "",
    type: "",
  };
}

export function evaluateSpaceAvailabilityBlockEditorInput(
  input: SpaceAvailabilityBlockEditorInput,
): SpaceAvailabilityBlockEditorResult {
  const blockId = normalizeSpaceAvailabilityBlockDocumentId(input.blockId);
  const spaceId = normalizeSpaceAvailabilityBlockDocumentId(input.spaceId);
  const type = input.type.trim();
  const startsAt = parseDateTimeLocal(input.startsAt);
  const endsAt = parseDateTimeLocal(input.endsAt);
  const label = input.label.trim();
  const labelAr = input.labelAr.trim();
  const internalReason = input.internalReason.trim();
  const publicReason = input.publicReason.trim();
  const errors: string[] = [];

  validateRequiredText("Availability block ID", blockId, idLimit, errors);
  validateRequiredText("Space ID", spaceId, idLimit, errors);
  validateRequiredText("English label", label, labelLimit, errors);
  validateOptionalText("Arabic label", labelAr, labelLimit, errors);
  validateOptionalText("Internal reason", internalReason, reasonLimit, errors);

  if (!spaceAvailabilityBlockTypes.includes(type as SpaceAvailabilityBlockType)) {
    errors.push("Availability block type must be unavailable, adminHold, or maintenance.");
  }

  if (!startsAt) {
    errors.push("Starts at must be a valid date and time.");
  }

  if (!endsAt) {
    errors.push("Ends at must be a valid date and time.");
  }

  if (startsAt && endsAt && startsAt >= endsAt) {
    errors.push("Ends at must be after starts at.");
  }

  if (publicReason !== "true" && publicReason !== "false") {
    errors.push("Public reason must be true or false.");
  }

  if (
    errors.length > 0 ||
    !startsAt ||
    !endsAt ||
    !spaceAvailabilityBlockTypes.includes(type as SpaceAvailabilityBlockType)
  ) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    draft: {
      blockId,
      endsAt,
      internalReason: internalReason || null,
      label: {
        ar: labelAr || null,
        en: label,
      },
      publicReason: publicReason === "true",
      source: "experience_thirty_nine",
      spaceId,
      startsAt,
      type: type as SpaceAvailabilityBlockType,
    },
  };
}

export function normalizeSpaceAvailabilityBlockDocumentId(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, idLimit);
}

function parseDateTimeLocal(value: string): Date | undefined {
  const trimmed = value.trim();

  if (!trimmed) {
    return undefined;
  }

  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function validateRequiredText(
  label: string,
  value: string,
  maxLength: number,
  errors: string[],
): void {
  if (!value) {
    errors.push(`${label} is required.`);
    return;
  }

  if (value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}

function validateOptionalText(
  label: string,
  value: string,
  maxLength: number,
  errors: string[],
): void {
  if (value && value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}
